import { folderColor } from '@constants/folderColor';
import { ETypeNote } from '@constants/typeNote';
import { ENoteTypeFile } from '@enums/NoteTypeFile';
import { type Note } from '@models/Note';
import WebClient from '@services/utils/WebClient';

import { type ICreateNoteInputData } from './dtos/createNote/InputData';
import { type IDeleteNoteInputData } from './dtos/deleteNote/InputData';
import { type IGetAllNotesInputData } from './dtos/getAllNotes/InputData';
import { type IGetAllNotesOutputData } from './dtos/getAllNotes/OutputData';
import { type IGetNoteDetailsInputData } from './dtos/getNoteDetails/InputData';
import { type IGetNoteDetailsOutputData } from './dtos/getNoteDetails/OutputData';
import { type IMoveNoteInputData } from './dtos/moveNote/InputData';
import { type IUpdateNoteInputData } from './dtos/updateNote/InputData';
import { type INoteRepository } from './INoteRepository';

interface ICreateBody {
  content: string;
  folder_id: string | null;
  presentation: string | null;
  title: string;
  type: ETypeNote;
}

interface IGetAllParams {
  folder_id?: string;
}
interface IGetAllResponse {
  list: {
    category: ENoteTypeFile;
    color: string | null;
    id: string;
    title: string;
  }[];
  path: {
    id: string | null;
    parent: string | null;
    title: string | null;
  }[];
}

interface IGetDetailsResponse {
  content: string;
  id: string;
  presentation: string | null;
  title: string;
  type: ETypeNote;
}

interface IMoveBody {
  folder_id: string | null;
}

interface IUpdateParams {
  id: number;
}

interface IUpdateBody {
  content: string;
  presentation: string | null;
  title: string;
  type: ETypeNote;
}

class NoteService implements INoteRepository {
  public async create(inputData: ICreateNoteInputData): Promise<void> {
    await WebClient.post<void, ICreateBody>({
      path: 'storages/notes',
      body: {
        presentation: inputData.presentation,
        content: inputData.content,
        type: inputData.type,
        title: inputData.title,
        folder_id: inputData.folderId,
      },
    });
  }

  public async delete(inputData: IDeleteNoteInputData): Promise<void> {
    await WebClient.delete<void>({
      path: `/storages/notes/${inputData.id}`,
    });
  }

  public async getAll(
    inputData: IGetAllNotesInputData,
  ): Promise<IGetAllNotesOutputData> {
    const params: IGetAllParams = {};
    if (inputData.folderId) {
      params.folder_id = inputData.folderId;
    }
    const response = await WebClient.get<IGetAllResponse, IGetAllParams>({
      path: 'storages',
      params,
    });
    const output: IGetAllNotesOutputData = {
      list: response.data.list.map((item) => {
        const note: Note = {
          color: item.color || folderColor.default,
          id: item.id,
          title: item.title,
          category: item.category,
        };
        return note;
      }),
      path: response.data.path.map((item) => ({
        id: item.id,
        parent: item.parent,
        title: item.title,
      })),
    };
    return output;
  }

  public async getDetails(
    inputData: IGetNoteDetailsInputData,
  ): Promise<IGetNoteDetailsOutputData> {
    const response = await WebClient.get<IGetDetailsResponse>({
      path: `storages/notes/${inputData.id}`,
    });
    const output: IGetNoteDetailsOutputData = {
      content: response.data.content,
      id: response.data.id,
      presentation: response.data.presentation,
      title: response.data.title,
      type: response.data.type,
    };
    return output;
  }

  public async move(inputData: IMoveNoteInputData): Promise<void> {
    await WebClient.patch<void, IMoveBody>({
      path: `storages/notes/${inputData.id}/move`,
      body: {
        folder_id: inputData.folderIdDestination,
      },
    });
  }

  public async update(inputData: IUpdateNoteInputData): Promise<void> {
    await WebClient.put<void, IUpdateBody, IUpdateParams>({
      path: `storages/notes/${inputData.id}`,
      body: {
        presentation: inputData.presentation,
        content: inputData.content,
        type: inputData.type,
        title: inputData.title,
      },
    });
  }
}

export default new NoteService();
